/**
 * The account button in the app shell's header (#371, reshaped by #581): one round button that opens a small menu
 * - "Signed in as ben", Settings, Contact us, Sign out.
 *
 * **It renders for every session, the launch-only one included** - that session has no username, so the menu
 * leaves out "Signed in as" and Sign out, but Settings stays: Settings -> My data is where §10.4's "find complete
 * deletion without developer help" lives now (it was a header link before #581), and a launch-only founder must
 * still be able to find it. Signing out of a launch session would be a trap anyway: the launch link works exactly
 * once, so there is no way back in. For that session Sign out is absent, not disabled.
 *
 * Settings goes to `/settings` -> `/settings/account`. A learner without feedback consent sees the required consent
 * screen there (the gate replaces the page), so that screen links to `/settings/data` and `/settings/contact` itself.
 *
 * Signing out (`useSignOut`, also used by Settings -> Account) discards the in-memory query cache and goes to
 * `/login`, even if the server answers `401` (the session was already gone, which is what the person wanted). If the
 * server cannot be reached the session may still be live, and saying "signed out" would be untrue - so that case
 * stays on the page and says so.
 *
 * Keyboard: Enter/Space/ArrowDown on the button opens it and focuses the first item; arrows, Home and End move;
 * Esc closes and returns focus to the button; Tab and a click outside close it.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ApiRequestError } from '../api/client.js';
import { queryKeys } from '../api/query-keys.js';
import { useMessages } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import { useApi } from './api-context.js';
import { LanguageSwitch } from './language-switch.js';

const messages = defineMessages({
  en: {
    account: 'Account',
    signedInAs: 'Signed in as',
    settings: 'Settings',
    history: 'Past homework',
    language: 'Language',
    contact: 'Contact us',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    signOutFailed:
      'DaVinci could not sign you out — it may not be reachable. You are still signed in. Try again.',
  },
  he: {
    account: 'החשבון',
    signedInAs: 'מחובר בתור',
    settings: 'הגדרות',
    history: 'שיעורי בית קודמים',
    language: 'שפה',
    contact: 'יצירת קשר',
    signOut: 'התנתקות',
    signingOut: 'מתנתק…',
    signOutFailed: 'DaVinci לא הצליח לנתק אותך — ייתכן שהשרת אינו זמין. החיבור עדיין פעיל. כדאי לנסות שוב.',
  },
});

/** The auth session, read the same way everywhere (same key: answered from the cache after the first read). */
export function useAuthSession() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.authSession, queryFn: () => api.authSession() });
}

/** Sign out: clear the cache and go to /login; a 401 means already out, so leave all the same. */
export function useSignOut() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => leave(),
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status === 401) leave();
    },
  });
  function leave(): void {
    queryClient.clear();
    void navigate('/login', { replace: true });
  }
  const failed = mutation.isError && !(mutation.error instanceof ApiRequestError && mutation.error.status === 401);
  return { signOut: () => mutation.mutate(), isPending: mutation.isPending, failed };
}

/** `withLanguage`: on a phone the EN · עב switch lives in this menu (MainPhone.dc.html), not in the top bar. */
export function AccountControl({ withLanguage = false }: { readonly withLanguage?: boolean }): React.JSX.Element {
  const t = useMessages(messages);
  const session = useAuthSession();
  const { signOut, isPending, failed } = useSignOut();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const username = session.isSuccess ? session.data.username : null;
  const initial = username === null ? undefined : Array.from(username)[0]?.toUpperCase();

  function items(): HTMLElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  }

  function close(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (open) items()[0]?.focus();
  }, [open]);

  // A click or touch outside closes it, without stealing focus from whatever was clicked.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: Event): void {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close(true);
      return;
    }
    if (!open) return;
    const list = items();
    const index = list.indexOf(document.activeElement as HTMLElement);
    let next: number | undefined;
    if (event.key === 'ArrowDown') next = (index + 1) % list.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + list.length) % list.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = list.length - 1;
    else if (event.key === 'Tab') setOpen(false);
    if (next !== undefined) {
      event.preventDefault();
      list[next]?.focus();
    }
  }

  return (
    <div className="account-menu" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        className="account-button"
        aria-label={t.account}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {initial === undefined ? (
          <span className="account-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" focusable="false">
              <circle cx="12" cy="8" r="4" fill="currentColor" />
              <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" fill="currentColor" />
            </svg>
          </span>
        ) : (
          <span className="account-avatar" aria-hidden="true">
            {initial}
          </span>
        )}
        <svg className="account-chevron" aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div className="account-popover">
          {username === null ? null : (
            <p className="account-popover-who">
              {t.signedInAs} <strong dir="ltr">{username}</strong>
            </p>
          )}
          {withLanguage ? (
            <div className="account-language">
              <span>{t.language}</span>
              <LanguageSwitch />
            </div>
          ) : null}
          <div role="menu" aria-label={t.account} ref={menuRef}>
            <Link role="menuitem" to="/settings" onClick={() => close(false)}>
              {t.settings}
            </Link>
            <Link role="menuitem" to="/homework" onClick={() => close(false)}>
              {t.history}
            </Link>
            <Link role="menuitem" to="/settings/contact" onClick={() => close(false)}>
              {t.contact}
            </Link>
            {username === null ? null : (
              <button type="button" role="menuitem" onClick={signOut} disabled={isPending}>
                {isPending ? t.signingOut : t.signOut}
              </button>
            )}
          </div>
          {failed ? (
            <p role="alert" className="account-popover-error">
              {t.signOutFailed}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
